import React, { useCallback, useEffect, useRef } from "react";
import { useState } from "react";
import {
  BadgeEuro,
  Check,
  ChevronDown,
  ChevronUp,
  CircuitBoard,
  Computer,
  Cpu,
  Fan,
  HardDrive,
  Keyboard,
  List,
  Mail,
  MapPin,
  MemoryStick,
  Microchip,
  Monitor,
  Mouse,
  Notebook,
  Package,
  PcCase,
  Phone,
  Power,
  Rocket,
  ShoppingCart,
  StickyNote,
  UserRound,
  Wifi,
  X,
} from "lucide-react";
import { API_URLS } from "../../App";
import api from "../../hooks/api";
import { motion } from "framer-motion";
import usePrintHtmlInNewWindow from "../../hooks/usePrintHtmlInNewWindow";
import CancelOfferConfirmation from "./CancelOfferConfirmation";
import ConfirmOrderConfirmation from "./ConfirmOrderConfirmation";
import AcceptOfferConfirmation from "./AcceptOfferConfirmation";
import { getStatusClass, getTranslate } from "../../helpers/Offer";
import { useLayoutContext } from "../../context/LayoutContext";
import { useNavigate } from "react-router-dom";
import NotesSection from "../Shared/NotesSection";
import OfferProposal from "../../model/OfferProposal";
import OfferExcelExportModal, {
  OfferExcelInternalField,
} from "./OfferExcelExportModal";

const ManageOffer = ({
  offer,
  initialUpdatedOffer,
}: {
  offer: OfferProposal;
  initialUpdatedOffer?: boolean;
}) => {
  const navigate = useNavigate();
  const {
    userSettings,
    setUserSettings,
    systemError,
    setSystemError,
    tasks,
    removeTask,
    loading,
    setLoading,
    error,
    setError,
    success,
    setSuccess,
    modalScreen,
    setModalScreen,
    page,
    setPage,
    isDevMode,
    modalRef,
  } = useLayoutContext();

  const { addTask } = useLayoutContext();
  const [offerProposal, setOfferProposal] = useState<OfferProposal>(offer);
  const [updatedOffer, setUpdatedOffer] = useState<boolean>(
    initialUpdatedOffer ?? false,
  );
  const [tabSelected, setTabSelected] = useState<
    "components" | "client" | "notes" | "order" | "review"
  >(!offer.status || offer.status == "in_progress" ? "client" : "components");
  const [showCancelOfferModal, setShowCancelOfferModal] =
    useState<boolean>(false);
  const [showConfirmOrderModal, setShowConfirmOrderModal] =
    useState<boolean>(false);
  const [showAcceptOfferModal, setShowAcceptOfferModal] =
    useState<boolean>(false);
  const [showExcelExportModal, setShowExcelExportModal] =
    useState<boolean>(false);
  const [excelExporting, setExcelExporting] = useState<boolean>(false);
  const [lastOfferClient, setLastOfferClient] = useState<Client | null>(null);
  const [showShippingAddress, setShowShippingAddress] = useState<boolean>(
    !offer.client.use_personal_as_shipping,
  );
  const [showBillingAddress, setShowBillingAddress] = useState<boolean>(
    !offer.client.use_personal_as_billing,
  );
  const [offerPreviewHtml, setOfferPreviewHtml] = useState<string>("");

  const updateClientField = (field: keyof Client, value: any) => {
    setUpdatedOffer(true);
    setOfferProposal((prev) => ({
      ...prev,
      client: {
        ...prev.client,
        [field]: value,
      },
    }));
  };

  const allowManageOffer = useCallback(() => {
    return (
      !offer.erp_client_id || offer.erp_client_id == userSettings?.client?.id
    );
  }, [offer.erp_client_id, userSettings?.client?.id]);

  useEffect(() => {
    const lastOfferClientRaw = localStorage.getItem("lastOfferClient");
    if (lastOfferClientRaw) {
      const lastOfferClientParsed = JSON.parse(lastOfferClientRaw);
      setLastOfferClient(lastOfferClientParsed);
    }
  }, [localStorage]);

  const fetchHistory = () => {
    if (
      !offerProposal.id ||
      !(userSettings?.isMainAccount() || userSettings?.isAdmin())
    )
      return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    api
      .get(`${API_URLS.OFFERS}/${offerProposal.id}/history`)
      .then((res) => {
        console.log("Histórico carregado com sucesso!", res.data);
        setOfferProposal((prev) => ({ ...prev, history: res.data || [] }));
      })
      .catch(() => {
        console.warn("Erro ao carregar histórico");
        setError("Erro ao carregar histórico. Tente novamente.");
      })
      .finally(() => setLoading(false));
  };

  const fetchOffer = (offerId: string, resetNotifications?: boolean) => {
    if (resetNotifications) {
      setError(null);
      setSuccess(null);
    }
    setLoading(true);
    api
      .get(`${API_URLS.OFFERS}/${offerId}`)
      .then((res) => {
        console.log("Proposta carregada com sucesso!", res.data);
        setOfferProposal(res.data as OfferProposal);
        setUpdatedOffer(false);
      })
      .catch(() => {
        console.warn("Erro ao carregar proposta");
        setError("Erro ao carregar proposta. Tente novamente.");
      })
      .finally(() => setLoading(false));
  };

  const saveClientOnLocalStorage = (offerProposal: OfferProposal) => {
    const client = offerProposal.client;
    if (
      client.id?.trim() !== "" ||
      client.name.trim() !== "" ||
      client.email?.trim() !== "" ||
      client.phone?.trim() !== "" ||
      client.vat?.trim() !== "" ||
      client.address?.trim() !== "" ||
      client.city?.trim() !== "" ||
      client.zip_code?.trim()
    ) {
      localStorage.setItem("lastOfferClient", JSON.stringify(client));
      setLastOfferClient(client);
    }
  };

  const generateOfferPreview = useCallback(async () => {
    try {
      const response =
        offerProposal.id && !updatedOffer
          ? await api.get(
              API_URLS.OFFERS + "/" + offerProposal.id + "/_actions/review",
            )
          : await api.post(
              API_URLS.OFFERS + "/_actions/preview",
              offerProposal,
            );
      console.log("Preview gerado com sucesso!", response.data);
      return response.data as string;
    } catch (error) {
      console.warn("Erro ao gerar preview:", error);
      setError("Erro ao gerar preview. Tente novamente.");
      return "";
    }
  }, [offerProposal, setError, allowManageOffer]);

  const handleCreateProposal = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);
    api
      .post(API_URLS.OFFERS + "/", { ...offerProposal, status: "in_progress" })
      .then((res) => {
        console.log("Proposta criada com sucesso!", res.data);

        setOfferProposal(res.data as OfferProposal);
        saveClientOnLocalStorage(res.data as OfferProposal);
        setUpdatedOffer(false);
        setTabSelected("components");
        setSuccess("Proposta criada com sucesso!");
      })
      .catch(() => {
        console.warn("Erro ao criar proposta");
        setError("Erro ao criar proposta. Tente novamente.");
      })
      .finally(() => setLoading(false));
  };

  const handleUpdateProposal = async (
    e: React.MouseEvent<HTMLButtonElement>,
  ) => {
    e.preventDefault();
    if (!offerProposal.id) return;
    setError(null);
    setSuccess(null);
    setLoading(true);
    api
      .put(API_URLS.OFFERS + "/" + offerProposal.id, offerProposal)
      .then((res) => {
        console.log("Proposta atualizada com sucesso!", res.data);
        setOfferProposal(res.data as OfferProposal);
        saveClientOnLocalStorage(res.data as OfferProposal);
        setUpdatedOffer(false);
        setSuccess("Proposta atualizada com sucesso!");
      })
      .catch((err) => {
        console.warn("Erro ao atualizar proposta:", err);
        setError("Erro ao atualizar proposta. Tente novamente.");
      })
      .finally(() => setLoading(null));
  };

  const handlePrintPropose = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!offerProposal.id) return;
    setLoading(true);
    setError(null);
    setSuccess(null);
    api
      .post(API_URLS.OFFERS + "/" + offerProposal.id + "/_actions/print")
      .then((response) => {
        console.log("Proposta preparada para impressão!", response.data);
        setSuccess("Proposta preparada para impressão!");
        usePrintHtmlInNewWindow(response.data, "Proposta");
      })
      .catch((error) => {
        console.warn("Erro ao preparar proposta para impressão:", error);
        setError("Erro ao preparar proposta para impressão. Tente novamente.");
      })
      .finally(() => {
        if (offerProposal.id) fetchOffer(offerProposal.id, false);
      });
  };

  const handleExportExcel = async (fields: OfferExcelInternalField[]) => {
    setExcelExporting(true);
    setLoading(true);
    setError(null);

    try {
      const response = await api.post(
        API_URLS.OFFERS + "/_actions/export-excel",
        offerProposal,
        {
          responseType: "blob",
          params: { internalFields: fields.join(",") },
        },
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `proposta_${offerProposal.external_id || offerProposal.compound.id}.xlsx`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setShowExcelExportModal(false);
    } catch (error: any) {
      console.warn("Erro ao exportar proposta para Excel:", error);
      setError(error.message || "Erro ao exportar proposta para Excel.");
    } finally {
      setExcelExporting(false);
      setLoading(null);
    }
  };

  const handleSendEmail = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!offerProposal) return;
    setError(null);
    setSuccess(null);
    setLoading(true);
    api
      .post(API_URLS.OFFERS + "/" + offerProposal.id + "/_actions/send-email", {
        destination: offerProposal.client.email,
      })
      .then((response) => {
        console.log("Email enviado com sucesso!", response.data);
        setSuccess("Email enviado com sucesso!");
      })
      .catch((error) => {
        setError(`Error a enviar email ${error.message}. Tente novamente.`);
        console.warn("Erro ao enviar email:", error);
      })
      .finally(() => {
        setLoading(null);
      });
  };

  const handleNewOfferProposal = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!offerProposal) return;

    addTask({
      name: "Alterar proposta",
      tip: {
        pc: "Alterar configuração dos componentes do PC ou Preço.",
        store: "Alterar para outro PC pré-configurado da loja.",
        comparar: "Alterar para outro PC pré-configurado.",
      },
      id: "offer::change-" + offerProposal.id,
      taskType: "offer::change",
      allowedModules: ["pc", "store", "comparar"],
      getContext: () => {
        return {
          offerProposal: offerProposal,
          setOfferProposal: setOfferProposal,
        };
      },
      onSwitch: () => {
        console.log("Abrindo modal para gerir proposta:", offerProposal.id);
        setModalScreen({
          state: "edit",
          title: `Gerir Proposta: #${offerProposal.external_id}`,
          children: <ManageOffer offer={offerProposal} />,
          onClose: () => {
            setUpdatedOffer(false);
            navigate("/offers");
          },
        });
      },
      onAdd: () => {
        navigate("/pc/" + offerProposal.compound.id);
        setModalScreen(null);
      },
    } as WorkingTask);
  };

  const handleConfirmOrder = () => {
    setShowConfirmOrderModal(false);
    confirmAcceptOffer();
  };

  const handleAcceptOffer = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!offerProposal) return;
    if (isHandlingOfferDisabled()) {
      setSuccess(null);
      setError(
        "Não pode aceitar esta proposta. Verifique os dados do cliente.",
      );
      return;
    }
    // Show confirmation modal instead of directly accepting
    setShowAcceptOfferModal(true);
  };

  const confirmAcceptOffer = () => {
    if (!offerProposal) return;
    setLoading(true);
    setError(null);
    setSuccess(null);
    api
      .post(API_URLS.OFFERS + "/" + offerProposal.id + "/_actions/accept", {})
      .then((response) => {
        console.log("Encomenda criada com sucesso!", response.data);
        setOfferProposal(response.data as OfferProposal);
        setUpdatedOffer(false);
        setTabSelected("order");
        setSuccess("Encomenda criada com sucesso!");
        if (userSettings?.isAdmin() || userSettings?.isCommercial()) {
          navigate("/orders");
          modalRef?.current?.close();
        }
      })
      .catch((error) => {
        console.warn("Erro ao aceitar a proposta:", error);
        setError("Erro ao aceitar a proposta. Tente novamente.");
      })
      .finally(() => {
        setLoading(null);
      });
  };

  const handleCancelOffer = async (cancelReason: CancelReason) => {
    if (!offerProposal.id) return;
    setError(null);
    setSuccess(null);
    setLoading(true);
    try {
      const res = await api.post(
        API_URLS.OFFERS + "/" + offerProposal.id + "/_actions/cancel",
        cancelReason,
      );
      console.log("Proposta cancelada com sucesso!", res.data);
      setOfferProposal(res.data as OfferProposal);
      saveClientOnLocalStorage(res.data as OfferProposal);
      setUpdatedOffer(false);
      setShowCancelOfferModal(false);
      setSuccess("Proposta cancelada com sucesso!");
    } catch (error) {
      console.warn("Erro ao cancelar proposta:", error);
      setError("Erro ao cancelar proposta. Tente novamente.");
    } finally {
      setLoading(null);
    }
  };

  const searchClientById = async (clientId: string) => {
    if (!clientId || clientId.trim() === "") return;
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(
        API_URLS.USERS + `_actions/get_erp_client?client_id=${clientId}`,
      );
      if (!response.data || !response.data.id) {
        setOfferProposal({
          ...offerProposal,
          match_client: false,
        });
        return;
      }
      if (offerProposal.client_type == "end_customer") {
        setSuccess("O cliente foi actualizado para revendedor.");
      }
      setOfferProposal({
        ...offerProposal,
        match_client: true,
        client_type: "reseller",
        client: {
          ...offerProposal.client,
          id: offerProposal.client.id || response.data.id,
          name: offerProposal.client.name || response.data.name,
          email: offerProposal.client.email || response.data.email,
          phone: offerProposal.client.phone || response.data.phone,
          vat: offerProposal.client.vat || response.data.vat,
          address: offerProposal.client.address || response.data.address,
          city: offerProposal.client.city || response.data.city,
          zip_code: offerProposal.client.zip_code || response.data.zip_code,
        },
      });
    } catch (error) {
      console.warn("Erro ao buscar cliente:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestCancelOffer = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setShowCancelOfferModal(true);
  };

  const isValidEmail = () => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+/;
    return re.test(String(offerProposal.client.email).toLowerCase());
  };
  const isValidPhone = () => {
    if (
      offerProposal.client?.phone?.startsWith("+351") ||
      offerProposal.client?.phone?.startsWith("00351") ||
      !offerProposal.client?.phone?.startsWith("+")
    ) {
      const phoneWithoutCountryCode =
        offerProposal.client?.phone?.replace(/^(\+|00)351/, "").trim() ?? "";
      const phoneWithoutCountryCodeDigits = phoneWithoutCountryCode.replace(
        /[\s\-()]/g,
        "",
      ); // replace whitespace, dashes, and parentheses
      if (phoneWithoutCountryCodeDigits.length !== 9) return false; // Must be exactly 9 digits

      const re = /^((91|92|93|96)\d{7}|(2)\d{8})$/;
      return re.test(phoneWithoutCountryCodeDigits);
    }
    const re = /^\+?[0-9\s\-()]+$/; // Basic phone number validation
    return re.test(String(offerProposal.client.phone).toLowerCase());
  };

  const isUpdateOfferEnabled = () => {
    if (!updatedOffer) return false;
    if (offerProposal.status == "requested") return false;
    return !isClientValid();
  };

  const isClientValid = () => {
    return (
      (userSettings?.isReseller() || offerProposal.client.id?.trim() != "") &&
      (!offerProposal.client?.email || isValidEmail()) &&
      (!offerProposal.client?.phone || isValidPhone())
    );
  };

  const isEmptyClient = (client: Client): boolean => {
    return (
      !client.id?.trim() &&
      !client.name.trim() &&
      !client.email?.trim() &&
      !client.phone?.trim() &&
      !client.vat?.trim() &&
      !client.address?.trim() &&
      !client.city?.trim() &&
      !client.zip_code?.trim()
    );
  };

  const isHandlingOfferDisabled = useCallback(() => {
    return (
      ((userSettings?.isCommercial() || userSettings?.isAdmin()) &&
        (offerProposal.status === "draft" ||
          offerProposal.status === "requested" ||
          !offerProposal.id ||
          isEmptyClient(offerProposal.client) ||
          !isClientValid())) ??
      false
    );
  }, [
    userSettings,
    offerProposal.status,
    offerProposal.id,
    offerProposal.client,
  ]);

  useEffect(() => {
    if (tasks.length > 0) {
      const changeOffersTasks = tasks.filter(
        (t) => t.taskType === "offer::change",
      );
      if (changeOffersTasks.length > 0) {
        changeOffersTasks.map((t) => {
          console.log("automatically removing task:", t.id);
          removeTask(t.id);
        });
      }
    }
  }, [tasks, offer?.id]);

  useEffect(() => {
    generateOfferPreview().then((html) => setOfferPreviewHtml(html));
  }, [generateOfferPreview]);

  /**
  useEffect(() => {
    if (!offerProposal.id) return;
    const intervalId = setInterval(() => {
      fetchHistory();
    }, 2000);
    return () => clearInterval(intervalId);
  }, [offerProposal.id]);
  **/
  const calculatedFinancials = React.useMemo(() => {
    if (!offerProposal?.compound) return null;

    const getWithoutVat = (price: any) => {
      if (price.without_vat && price.without_vat > 0) return price.without_vat;
      const vatRate =
        price.vat_rate !== undefined && price.vat_rate !== null
          ? price.vat_rate
          : 23;
      return price.value / (1 + vatRate / 100);
    };

    let totalDiffs = { resell: 0, without_vat: 0, value: 0 };
    const componentRows = Object.entries(
      offerProposal.compound.components || {},
    ).map(([type, components]) => {
      // Find selected component
      let selectedComp = components.find((c) => {
        if (
          offerProposal.compound.selected_components &&
          offerProposal.compound.selected_components[type]
        ) {
          return c.id === offerProposal.compound.selected_components[type]!.id;
        }
        return c.selected;
      });

      // Find base component
      const baseComp = components.find((c) => c.baseComponent);

      if (!selectedComp) return null;
      if (selectedComp.name === "NENHUMA OPÇÃO SELECIONADA") return null;

      const isBase =
        selectedComp.baseComponent || selectedComp.id === baseComp?.id;

      let diff = { resell: 0, without_vat: 0, value: 0 };

      if (!isBase) {
        const selectedWithoutVat = getWithoutVat(selectedComp.price);
        const baseWithoutVat = baseComp ? getWithoutVat(baseComp.price) : 0;

        diff.resell =
          (selectedComp.price.resell || 0) - (baseComp?.price.resell || 0);
        diff.without_vat = selectedWithoutVat - baseWithoutVat;
        diff.value =
          (selectedComp.price.value || 0) - (baseComp?.price.value || 0);

        totalDiffs.resell += diff.resell;
        totalDiffs.without_vat += diff.without_vat;
        totalDiffs.value += diff.value;
      }

      return {
        type,
        name: selectedComp.name,
        isBase,
        diff,
      };
    });

    // Derived Base Price = Total Price - Total Diffs
    const totalWithoutVat = getWithoutVat(offerProposal.compound.price);

    const derivedBasePrice = {
      resell: (offerProposal.compound.price.resell || 0) - totalDiffs.resell,
      without_vat: totalWithoutVat - totalDiffs.without_vat,
      value: (offerProposal.compound.price.value || 0) - totalDiffs.value,
    };

    return { derivedBasePrice, componentRows };
  }, [offerProposal]);

  const icons = {
    Cpu: <Cpu className="inline mr-1" />,
    Gpu: <Microchip className="inline mr-1" />,
    CircuitBoard: <CircuitBoard className="inline mr-1" />,
    MemoryStick: <MemoryStick className="inline mr-1" />,
    HardDrive: <HardDrive className="inline mr-1" />,
    Fan: <Fan className="inline mr-1" />,
    PcCase: <PcCase className="inline mr-1" />,
    Wifi: <Wifi className="inline mr-1" />,
    Monitor: <Monitor className="inline mr-1" />,
    Keyboard: <Keyboard className="inline mr-1" />,
    Mouse: <Mouse className="inline mr-1" />,
    Power: <Power className="inline mr-1" />,
    Rocket: <Rocket className="inline mr-1" />,
  };

  if (!offer.compound) {
    return (
      <div className="error-container">
        <p className="title is-4">Configuração de PC não encontrada.</p>
      </div>
    );
  }

  return (
    <>
      <div className="overflow-y-auto">
        <form>
          <div className="container box min-h-[65vh]">
            {!allowManageOffer() && (
              <div className="notification is-warning">
                <p>
                  Não tem permissão para gerir esta proposta. Pode apenas
                  visualizar.
                </p>
              </div>
            )}
            <div className="proposal-review mb-10">
              <p className="title is-4">Proposta de Configuração de PC</p>
              {userSettings?.isOperatorUser() && (
                <p className="subtitle is-6">
                  <strong>Configuração:</strong> {offerProposal.compound.name}{" "}
                  <br />
                  <strong>Preço Total:</strong>{" "}
                  {offerProposal.client_type === "end_customer"
                    ? offerProposal.compound.price.value.toFixed(2)
                    : (offerProposal.compound.price.resell ?? 0).toFixed(
                        2,
                      )}{" "}
                  € <br />
                  <strong>Estado:</strong>
                  <span className={`m-2 p-1 ${getStatusClass(offer.status)}`}>
                    {getTranslate(offerProposal.status)}
                  </span>
                  {offerProposal.applied_campaign_name && (
                    <>
                      <br />
                      <strong>Campanha:</strong>{" "}
                      {offerProposal.applied_campaign_name}
                    </>
                  )}
                </p>
              )}
              {userSettings?.isOperatorUser() && (
                <p className="subtitle is-6 visibility-field flex items-center">
                  <strong>Visibilidade:</strong>{" "}
                  <select
                    id="visibility"
                    className="select"
                    value={offerProposal.visibility ?? "internal"}
                    onChange={(e) => {
                      setUpdatedOffer(true);
                      setOfferProposal({
                        ...offerProposal,
                        visibility: e.target.value as
                          | "public"
                          | "private"
                          | "internal",
                      });
                    }}
                  >
                    <option value="public" disabled={true}>
                      Pública
                    </option>
                    <option value="private">Privada</option>
                    <option value="internal">Interna</option>
                  </select>
                  <span className={`m-2 p-1 text-shadow-slate-700 text-sm`}>
                    {offerProposal.visibility === "public"
                      ? "Visível para todos os utilizadores"
                      : offerProposal.visibility === "private"
                        ? "Visível apenas para o utilizador que criou ou alterou a proposta"
                        : "Visível apenas para utilizadores internos"}
                  </span>
                </p>
              )}
            </div>
            <div className="tabs is-boxed">
              <ul>
                {(userSettings?.isOperatorUser() ||
                  offerProposal.status !== "in_progress") && (
                  <li
                    className={tabSelected === "components" ? "is-active" : ""}
                    onClick={() => setTabSelected("components")}
                  >
                    <a>
                      <span className="icon is-small">
                        <Computer className="inline mr-1" />
                      </span>
                      <span>Pre-visualizar</span>
                    </a>
                  </li>
                )}
                {/*
                {(userSettings?.isOperatorUser() ||
                  offerProposal.status !== "in_progress") && (
                    <li
                      className={tabSelected === "review" ? "is-active" : ""}
                      onClick={() => setTabSelected("review")}
                    >
                      <a>
                        <span className="icon is-small">
                          <List className="inline mr-1" />
                        </span>
                        <span>Resumo Financeiro</span>
                      </a>
                    </li>
                  )}
                  */}
                <li
                  className={tabSelected === "client" ? "is-active" : ""}
                  onClick={() => setTabSelected("client")}
                >
                  <a>
                    <span className="icon is-small">
                      <UserRound className="inline mr-1" />
                    </span>
                    <span>Cliente</span>
                  </a>
                </li>
                {offerProposal.id && (
                  <li
                    className={tabSelected === "notes" ? "is-active" : ""}
                    onClick={() => setTabSelected("notes")}
                  >
                    <a>
                      <span className="icon is-small">
                        <StickyNote className="inline mr-1" />
                      </span>
                      <span>Notas</span>
                    </a>
                  </li>
                )}
                {(userSettings?.isAdmin() || userSettings?.isMainAccount()) &&
                  offerProposal.status !== "draft" && (
                    <li
                      className={tabSelected === "order" ? "is-active" : ""}
                      onClick={() => {
                        if (offerProposal.history?.length == 0) fetchHistory();
                        setTabSelected("order");
                      }}
                    >
                      <a>
                        <span className="icon is-small">
                          <ShoppingCart className="inline mr-1" />
                        </span>
                        <span>Histórico/Tracking</span>
                      </a>
                    </li>
                  )}
              </ul>
            </div>
            <div
              className={`content min-h-screen flex flex-col ${tabSelected !== "components" ? "is-hidden" : ""}`}
            >
              <iframe
                srcDoc={offerPreviewHtml}
                className="w-full h-full"
                style={{ flex: 1, width: "100%", height: "100%" }}
              />
            </div>
            <div
              className={`content ${tabSelected !== "review" ? "is-hidden" : ""}`}
            >
              <div className="box">
                <h3 className="title is-5 mb-4">
                  Detalhe Financeiro dos Componentes
                </h3>
                <div className="table-container">
                  <table className="table is-fullwidth is-striped is-hoverable">
                    <thead>
                      <tr>
                        <th>Componente</th>
                        <th className="has-text-right">Custo (Revenda)</th>
                        <th className="has-text-right">Venda (s/ IVA)</th>
                        <th className="has-text-right">Venda (c/ IVA)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {calculatedFinancials && (
                        <>
                          {/* Base Compound Price (Derived) */}
                          <tr className="is-selected">
                            <td>
                              <strong>
                                {offerProposal.compound.name} (Base)
                              </strong>
                            </td>
                            <td className="has-text-right">
                              {calculatedFinancials.derivedBasePrice.resell.toFixed(
                                2,
                              )}{" "}
                              €
                            </td>
                            <td className="has-text-right">
                              {calculatedFinancials.derivedBasePrice.without_vat.toFixed(
                                2,
                              )}{" "}
                              €
                            </td>
                            <td className="has-text-right">
                              {calculatedFinancials.derivedBasePrice.value.toFixed(
                                2,
                              )}{" "}
                              €
                            </td>
                          </tr>

                          {/* Component Rows */}
                          {calculatedFinancials.componentRows.map((row) => {
                            if (!row) return null;
                            return (
                              <tr key={row.type}>
                                <td>
                                  <span className="text-sm text-gray-500 block uppercase text-xs font-bold">
                                    {row.type}
                                  </span>
                                  {row.name}
                                </td>
                                <td className="has-text-right">
                                  {row.isBase ? (
                                    <span className="text-gray-500 text-xs">
                                      Incluído
                                    </span>
                                  ) : (
                                    <span>
                                      {row.diff.resell > 0 ? "+" : ""}
                                      {row.diff.resell.toFixed(2)} €
                                    </span>
                                  )}
                                </td>
                                <td className="has-text-right">
                                  {row.isBase ? (
                                    <span className="text-gray-500 text-xs">
                                      Incluído
                                    </span>
                                  ) : (
                                    <span>
                                      {row.diff.without_vat > 0 ? "+" : ""}
                                      {row.diff.without_vat.toFixed(2)} €
                                    </span>
                                  )}
                                </td>
                                <td className="has-text-right">
                                  {row.isBase ? (
                                    <span className="text-gray-500 text-xs">
                                      Incluído
                                    </span>
                                  ) : (
                                    <span>
                                      {row.diff.value > 0 ? "+" : ""}
                                      {row.diff.value.toFixed(2)} €
                                    </span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </>
                      )}
                    </tbody>
                    <tfoot>
                      <tr>
                        <th>TOTAL</th>
                        <th className="has-text-right">
                          {(offerProposal.compound.price.resell || 0).toFixed(
                            2,
                          )}{" "}
                          €
                        </th>
                        <th className="has-text-right">
                          {(
                            offerProposal.compound.price.without_vat || 0
                          ).toFixed(2)}{" "}
                          €
                        </th>
                        <th className="has-text-right">
                          {(offerProposal.compound.price.value || 0).toFixed(2)}{" "}
                          €
                        </th>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
            <div
              className={`content ${tabSelected !== "client" ? "is-hidden" : ""}`}
            >
              {userSettings?.isOperatorUser() &&
                lastOfferClient &&
                isEmptyClient(offerProposal.client) && (
                  <div className="notification is-info flex items-center justify-between">
                    <span>
                      Último cliente utilizado:{" "}
                      {(userSettings?.isAdmin() ||
                        userSettings?.isCommercial()) &&
                        lastOfferClient.id && (
                          <strong className="mr-2">
                            Cliente: {lastOfferClient.id}
                          </strong>
                        )}
                      <strong className="mr-2">{lastOfferClient.name}</strong>
                      {lastOfferClient.vat && (
                        <span>NIF: ({lastOfferClient.vat})</span>
                      )}
                      {lastOfferClient.email && (
                        <span>Email: {lastOfferClient.email}</span>
                      )}
                    </span>
                    <button
                      className="button is-small is-primary is-right"
                      onClick={(e) => {
                        e.preventDefault();
                        setOfferProposal({
                          ...offerProposal,
                          client: lastOfferClient,
                        });
                        setUpdatedOffer(true);
                      }}
                    >
                      Reutilizar Cliente
                    </button>
                  </div>
                )}
              {(userSettings?.isCommercial() || userSettings?.isAdmin()) &&
                !offerProposal.match_client &&
                offerProposal.client.id && (
                  <div className="notification is-warning flex items-center justify-between">
                    <span>
                      {offerProposal.client_type === "reseller" &&
                        "O cliente não foi encontrado. "}
                      Por favor, verifique os dados e confirme que o cliente for
                      inserido no sistema e é válido.
                    </span>
                  </div>
                )}
              {(userSettings?.isCommercial() || userSettings?.isAdmin()) &&
                offerProposal.id &&
                !offerProposal.client.id && (
                  <div className="notification is-warning flex items-center justify-between">
                    <span>
                      O cliente ainda não foi definido. Por favor, insira os
                      dados do cliente para continuar.
                    </span>
                  </div>
                )}
              {(userSettings?.isCommercial() || userSettings?.isAdmin()) && (
                <div className="field is-horizontal">
                  <div className="field-label is-normal">
                    <label className="label">Tipo</label>
                  </div>
                  <div className="field-body">
                    <div className="field">
                      <p className="control is-expanded has-icons-left">
                        <input
                          type="radio"
                          value="end_customer"
                          name="client_type_end_customer"
                          id="client_type_end_customer"
                          checked={offerProposal.client_type === "end_customer"}
                          onChange={(e) => {
                            setUpdatedOffer(true);
                            setOfferProposal({
                              ...offerProposal,
                              client_type: e.target.value as "end_customer",
                            });
                          }}
                        />
                        <label
                          className="radio ml-2"
                          htmlFor="client_type_end_customer"
                        >
                          Cliente Final
                        </label>
                      </p>
                    </div>
                    <div className="field">
                      <p className="control is-expanded has-icons-left">
                        <input
                          type="radio"
                          value="reseller"
                          name="client_type_reseller"
                          id="client_type_reseller"
                          checked={offerProposal.client_type === "reseller"}
                          onChange={(e) => {
                            setUpdatedOffer(true);
                            setOfferProposal({
                              ...offerProposal,
                              client_type: e.target.value as "reseller",
                            });
                          }}
                        />
                        <label
                          className="radio ml-2"
                          htmlFor="client_type_reseller"
                        >
                          Revendedor
                        </label>
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="field is-horizontal">
                <div className="field-label is-normal">
                  <label className="label">Cliente</label>
                </div>
                <div className="field-body">
                  {(userSettings?.isCommercial() ||
                    userSettings?.isAdmin()) && (
                    <div className="field">
                      <p className="control is-expanded has-icons-left">
                        <input
                          className="input"
                          placeholder="Número de Cliente"
                          value={offerProposal.client.id ?? ""}
                          onChange={(e) =>
                            updateClientField("id", e.target.value)
                          }
                          onBlur={(e) => {
                            if (e.target.value) {
                              searchClientById(e.target.value.trim());
                            }
                          }}
                        />
                        <span className="icon is-small is-left">
                          <Notebook className="inline mr-1" />
                        </span>
                      </p>
                    </div>
                  )}

                  <div className="field" style={{ width: "60%" }}>
                    <p className="control is-expanded has-icons-left">
                      <input
                        className="input"
                        type="text"
                        placeholder="Nome do Cliente"
                        value={offerProposal.client.name ?? ""}
                        onChange={(e) =>
                          updateClientField("name", e.target.value)
                        }
                      />
                      <span className="icon is-small is-left">
                        <UserRound className="inline mr-1" />
                      </span>
                    </p>
                  </div>
                  <div className="field">
                    <p className="control is-expanded has-icons-left">
                      <input
                        className="input"
                        type="text"
                        placeholder="NIF do Cliente"
                        value={offerProposal.client.vat ?? ""}
                        onChange={(e) =>
                          updateClientField("vat", e.target.value)
                        }
                      />
                      <span className="icon is-small is-left">
                        <BadgeEuro className="inline mr-1" />
                      </span>
                    </p>
                  </div>
                </div>
              </div>
              <div className="field is-horizontal">
                <div className="field-label is-normal">
                  <label className="label">Morada</label>
                </div>
                <div className="field-body">
                  <div className="field">
                    <p className="control is-expanded">
                      <textarea
                        rows={3}
                        className="textarea"
                        placeholder="Morada do Cliente"
                        value={offerProposal.client.address ?? ""}
                        onChange={(e) =>
                          updateClientField("address", e.target.value)
                        }
                      />
                    </p>
                  </div>
                </div>
              </div>
              <div className="field is-horizontal">
                <div className="field-label"></div>
                <div className="field-body">
                  <div className="field">
                    <p className="control is-expanded">
                      <input
                        className="input"
                        placeholder="Codigo Postal"
                        value={offerProposal.client.zip_code ?? ""}
                        onChange={(e) =>
                          updateClientField("zip_code", e.target.value)
                        }
                      />
                    </p>
                  </div>
                  <div className="field">
                    <p className="control is-expanded">
                      <input
                        className="input"
                        type="text"
                        placeholder="Cidade"
                        value={offerProposal.client.city ?? ""}
                        onChange={(e) =>
                          updateClientField("city", e.target.value)
                        }
                      />
                    </p>
                  </div>
                </div>
              </div>
              <div className="field is-horizontal">
                <div className="field-label is-normal">
                  <label className="label">Contacto</label>
                </div>
                <div className="field-body">
                  <div className="field">
                    <p className="control is-expanded has-icons-left has-icons-right">
                      <input
                        className="input"
                        type="tel"
                        placeholder="Telefone"
                        value={offerProposal.client.phone ?? ""}
                        onChange={(e) =>
                          updateClientField("phone", e.target.value)
                        }
                      />
                      <span className="icon is-small is-left">
                        <Phone className="inline mr-1" />
                      </span>
                      <span className={`icon is-small is-right`}>
                        {!!offerProposal.client.phone && isValidPhone() ? (
                          <Check className="inline mr-1 text-green-500" />
                        ) : !!offerProposal.client.phone ? (
                          <X className="inline mr-1 text-red-500" />
                        ) : (
                          <></>
                        )}
                      </span>
                    </p>
                  </div>
                  <div className="field">
                    <p className="control is-expanded has-icons-left has-icons-right">
                      <input
                        className="input"
                        type="email"
                        placeholder="Email"
                        value={offerProposal.client.email ?? ""}
                        onChange={(e) =>
                          updateClientField("email", e.target.value)
                        }
                      />
                      <span className="icon is-small is-left">
                        <Mail className="inline mr-1" />
                      </span>
                      <span className={`icon is-small is-right`}>
                        {!!offerProposal.client.email && isValidEmail() ? (
                          <Check className="inline mr-1 text-green-500" />
                        ) : !!offerProposal.client.email ? (
                          <X className="inline mr-1 text-red-500" />
                        ) : (
                          <></>
                        )}
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Shipping Address Section */}
              <div className="field is-horizontal mt-5">
                <div className="field-label is-normal">
                  <label className="label"></label>
                </div>
                <div className="field-body">
                  <div className="field">
                    <button
                      type="button"
                      className="button is-light is-fullwidth"
                      onClick={() => {
                        const newValue = !showShippingAddress;
                        setShowShippingAddress(newValue);
                        updateClientField(
                          "use_personal_as_shipping",
                          !newValue,
                        );
                      }}
                    >
                      <Package className="inline mr-2" size={18} />
                      Morada de Envio
                      {showShippingAddress ? (
                        <ChevronUp className="inline ml-2" size={18} />
                      ) : (
                        <ChevronDown className="inline ml-2" size={18} />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {showShippingAddress && (
                <>
                  <div className="field is-horizontal">
                    <div className="field-label is-normal">
                      <label className="label">Morada</label>
                    </div>
                    <div className="field-body">
                      <div className="field">
                        <p className="control is-expanded has-icons-left">
                          <textarea
                            rows={2}
                            className="textarea"
                            placeholder="Morada de Envio"
                            value={offerProposal.client.shipping_address ?? ""}
                            onChange={(e) =>
                              updateClientField(
                                "shipping_address",
                                e.target.value,
                              )
                            }
                          />
                          <span className="icon is-small is-left">
                            <MapPin className="inline" />
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="field is-horizontal">
                    <div className="field-label"></div>
                    <div className="field-body">
                      <div className="field">
                        <p className="control is-expanded">
                          <input
                            className="input"
                            placeholder="Código Postal"
                            value={offerProposal.client.shipping_zip_code ?? ""}
                            onChange={(e) =>
                              updateClientField(
                                "shipping_zip_code",
                                e.target.value,
                              )
                            }
                          />
                        </p>
                      </div>
                      <div className="field">
                        <p className="control is-expanded">
                          <input
                            className="input"
                            placeholder="Cidade"
                            value={offerProposal.client.shipping_city ?? ""}
                            onChange={(e) =>
                              updateClientField("shipping_city", e.target.value)
                            }
                          />
                        </p>
                      </div>
                      <div className="field">
                        <p className="control is-expanded">
                          <input
                            className="input"
                            placeholder="País"
                            value={
                              offerProposal.client.shipping_country ??
                              "Portugal"
                            }
                            onChange={(e) =>
                              updateClientField(
                                "shipping_country",
                                e.target.value,
                              )
                            }
                          />
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Billing Address Section */}
              <div className="field is-horizontal mt-4">
                <div className="field-label is-normal">
                  <label className="label"></label>
                </div>
                <div className="field-body">
                  <div className="field">
                    <button
                      type="button"
                      className="button is-light is-fullwidth"
                      onClick={() => {
                        const newValue = !showBillingAddress;
                        setShowBillingAddress(newValue);
                        updateClientField("use_personal_as_billing", !newValue);
                      }}
                    >
                      <BadgeEuro className="inline mr-2" size={18} />
                      Morada Fiscal / Faturação
                      {showBillingAddress ? (
                        <ChevronUp className="inline ml-2" size={18} />
                      ) : (
                        <ChevronDown className="inline ml-2" size={18} />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {showBillingAddress && (
                <>
                  <div className="field is-horizontal">
                    <div className="field-label is-normal">
                      <label className="label">Morada</label>
                    </div>
                    <div className="field-body">
                      <div className="field">
                        <p className="control is-expanded has-icons-left">
                          <textarea
                            rows={2}
                            className="textarea"
                            placeholder="Morada Fiscal"
                            value={offerProposal.client.billing_address ?? ""}
                            onChange={(e) =>
                              updateClientField(
                                "billing_address",
                                e.target.value,
                              )
                            }
                          />
                          <span className="icon is-small is-left">
                            <MapPin className="inline" />
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="field is-horizontal">
                    <div className="field-label"></div>
                    <div className="field-body">
                      <div className="field">
                        <p className="control is-expanded">
                          <input
                            className="input"
                            placeholder="Código Postal"
                            value={offerProposal.client.billing_zip_code ?? ""}
                            onChange={(e) =>
                              updateClientField(
                                "billing_zip_code",
                                e.target.value,
                              )
                            }
                          />
                        </p>
                      </div>
                      <div className="field">
                        <p className="control is-expanded">
                          <input
                            className="input"
                            placeholder="Cidade"
                            value={offerProposal.client.billing_city ?? ""}
                            onChange={(e) =>
                              updateClientField("billing_city", e.target.value)
                            }
                          />
                        </p>
                      </div>
                      <div className="field">
                        <p className="control is-expanded">
                          <input
                            className="input"
                            placeholder="País"
                            value={
                              offerProposal.client.billing_country ?? "Portugal"
                            }
                            onChange={(e) =>
                              updateClientField(
                                "billing_country",
                                e.target.value,
                              )
                            }
                          />
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="field is-horizontal">
                    <div className="field-label is-normal">
                      <label className="label">NIF para Faturação</label>
                    </div>
                    <div className="field-body">
                      <div className="field">
                        <p className="control is-expanded has-icons-left">
                          <input
                            className="input"
                            placeholder="NIF para Faturação"
                            value={
                              offerProposal.client.billing_vat_number ?? ""
                            }
                            onChange={(e) =>
                              updateClientField(
                                "billing_vat_number",
                                e.target.value,
                              )
                            }
                          />
                          <span className="icon is-small is-left">
                            <BadgeEuro className="inline" />
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="field is-horizontal">
                    <div className="field-label is-normal">
                      <label className="label">Nome Fiscal</label>
                    </div>
                    <div className="field-body">
                      <div className="field">
                        <p className="control is-expanded has-icons-left">
                          <input
                            className="input"
                            placeholder="Nome para Faturação"
                            value={offerProposal.client.billing_name ?? ""}
                            onChange={(e) =>
                              updateClientField("billing_name", e.target.value)
                            }
                          />
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
            <div
              className={`content ${tabSelected !== "notes" ? "is-hidden" : ""}`}
            >
              <NotesSection
                entityId={Number(offerProposal.id) || 0}
                entityType="offer"
                showVisibilityOption={offerProposal.client_type === "reseller"}
              />
            </div>
            <div
              className={`content ${tabSelected !== "order" ? "is-hidden" : ""}`}
            >
              <p className="title is-5">Histórico da Proposta</p>

              {offerProposal.history && offerProposal.history.length > 0 ? (
                <ul className="list-none p-0">
                  {offerProposal.history.map((item, index) => (
                    <li key={index} className="mb-2">
                      <span className="text-gray-600">
                        {new Date(item.created_at).toLocaleString()}
                      </span>{" "}
                      - <span className="font-bold">{item.message}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>Nenhum histórico disponível.</p>
              )}
            </div>
          </div>
        </form>

        <OfferExcelExportModal
          open={showExcelExportModal}
          exporting={excelExporting}
          onClose={() => setShowExcelExportModal(false)}
          onExport={handleExportExcel}
        />

        {showCancelOfferModal && (
          <CancelOfferConfirmation
            offerId={offerProposal.id ?? null}
            onClose={() => setShowCancelOfferModal(false)}
            openModal={showCancelOfferModal}
            handleCancelProposal={handleCancelOffer}
          />
        )}

        {showConfirmOrderModal && (
          <ConfirmOrderConfirmation
            openModal={showConfirmOrderModal}
            onClose={() => setShowConfirmOrderModal(false)}
            onConfirm={handleConfirmOrder}
            offer={offerProposal}
          />
        )}

        {showAcceptOfferModal && (
          <AcceptOfferConfirmation
            openModal={showAcceptOfferModal}
            onClose={() => setShowAcceptOfferModal(false)}
            onConfirm={confirmAcceptOffer}
            offerProposal={offerProposal}
          />
        )}
      </div>

      <div className="sticky bottom-0 bg-gray-900/90 backdrop-blur-md text-white border-t modal-footer">
        <footer className={`p-4 ${loading ? "cursor-not-allowed" : ""}`}>
          <div className="flex is-justify-content-center not-printable min-h-[40px]">
            {!allowManageOffer() ? (
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded-full shadow-lg items-center gap-2"
                onClick={() => {
                  setModalScreen(null);
                  setOfferProposal({ ...offerProposal, id: undefined });
                  setUpdatedOffer(false);
                  navigate("/offers");
                }}
              >
                Voltar
              </motion.button>
            ) : (
              <>
                {!offerProposal.id && (
                  <>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      className="bg-blue-500 hover:bg-blue-700 disabled:bg-blue-200 text-white font-bold py-2 px-4 rounded-full shadow-lg items-center gap-2"
                      disabled={!isClientValid()}
                      onClick={handleCreateProposal}
                    >
                      {userSettings?.isNormalUser()
                        ? "Continuar"
                        : "Criar Proposta"}
                    </motion.button>
                    {(userSettings?.isAdmin() ||
                      userSettings?.isCommercial()) && (
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        className="bg-green-700 hover:bg-green-800 text-white font-bold py-2 px-4 rounded-full shadow-lg items-center gap-2 ml-4"
                        onClick={() => setShowExcelExportModal(true)}
                      >
                        Exportar Excel
                      </motion.button>
                    )}
                  </>
                )}
                {!!offerProposal.id && updatedOffer && (
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className="bg-blue-500 hover:bg-blue-700  disabled:bg-blue-200 text-white font-bold py-2 px-4 rounded-full shadow-lg items-center gap-2"
                    onClick={handleUpdateProposal}
                    disabled={isUpdateOfferEnabled()}
                  >
                    {userSettings?.isNormalUser()
                      ? "Confirmar alteração"
                      : "Guardar Proposta"}
                  </motion.button>
                )}

                {!!offerProposal.id && !updatedOffer && (
                  <>
                    {["draft", "requested", "in_progress"].includes(
                      offerProposal.status,
                    ) && (
                      <>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded-full shadow-lg items-center gap-2 ml-4"
                          onClick={handlePrintPropose}
                        >
                          Imprimir
                        </motion.button>

                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          className={`bg-green-700 hover:bg-green-800 text-white font-bold py-2 px-4 rounded-full shadow-lg items-center gap-2 ml-4 ${
                            !offerProposal.client.email
                              ? "opacity-50 cursor-not-allowed"
                              : ""
                          }`}
                          disabled={
                            !offerProposal.client.email || !isValidEmail()
                          }
                          onClick={handleSendEmail}
                        >
                          {" "}
                          Enviar Email
                        </motion.button>
                      </>
                    )}

                    {["in_progress", "requested", "sent"].includes(
                      offerProposal.status,
                    ) && (
                      <>
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          className="bg-yellow-500 hover:bg-yellow-700 text-white font-bold py-2 px-4 rounded-full shadow-lg items-center gap-2 ml-4"
                          onClick={handleNewOfferProposal}
                        >
                          Alterar Proposta
                        </motion.button>

                        {userSettings?.isOperatorUser() && (
                          <>
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-full shadow-lg items-center gap-2 ml-4"
                              onClick={(e) => {
                                e.preventDefault();
                                setShowConfirmOrderModal(true);
                              }}
                            >
                              Encomendar
                            </motion.button>

                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              className="bg-red-400 hover:bg-red-600 text-white font-bold py-2 px-4 rounded-full shadow-lg items-center gap-2 ml-4"
                              onClick={handleRequestCancelOffer}
                            >
                              Cancelar
                            </motion.button>
                          </>
                        )}
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </footer>
      </div>
    </>
  );
};

export default ManageOffer;
